import type { UUID } from '@appcues/domain-types';
import type { Action } from 'ext/lib/collections';
import { createCollectionReducer, DELETED } from 'ext/lib/collections';
import {
  TYPE,
  SET_LOCALE,
  REMOVE_DRAFT_LOCALE,
  CLEAR_DRAFT_LOCALES,
} from './actions';
import type { LocalesState, Locale, LocalesCollection } from './types';

const collectionReducer = createCollectionReducer(TYPE);

const initialState: LocalesState = {
  active: null,
  draftLocales: [],
};

export default function reducer(
  state: LocalesState = initialState,
  action: Action
): LocalesState {
  if (action.type === SET_LOCALE) {
    return {
      ...state,
      active: action.payload.id,
      draftLocales: [...(state?.draftLocales ?? []), action.payload.id],
    };
  }

  if (action.type === REMOVE_DRAFT_LOCALE) {
    return {
      ...state,
      draftLocales: (state.draftLocales ?? []).filter(
        id => id !== action.payload.id
      ),
    };
  }

  if (action.type === CLEAR_DRAFT_LOCALES) {
    return {
      ...state,
      draftLocales: [],
    };
  }

  return collectionReducer(state as any, action) as LocalesState;
}

export const getLanguageCode = (value: Locale): string | null => {
  try {
    return value.conditions.and[0].properties.value.split('\n')[0];
  } catch {
    return null;
  }
};

export const selectLocales = <T extends { locales: LocalesState }>(
  state: T
): LocalesCollection => {
  const entriesWithAddedLangCode = Object.entries(state[TYPE]).map(
    ([key, value]) => {
      if (key === 'active' || key === 'draftLocales') return [key, value];
      return [
        key,
        {
          ...(value as Locale),
          languageCode: getLanguageCode(value as Locale),
        },
      ];
    }
  );

  return Object.fromEntries(
    entriesWithAddedLangCode.filter(
      ([key, locale]) =>
        key !== 'active' &&
        key !== 'draftLocales' &&
        !(locale as any)[DELETED] &&
        (locale as any)?.name
    )
  ) as LocalesCollection;
};

export const selectLocale = <T extends { locales: LocalesState }>(
  state: T,
  id: UUID
): Locale | undefined => state[TYPE][id] as Locale | undefined;

export const selectActiveLocale = <T extends { locales: LocalesState }>(
  state: T
): Locale | undefined => {
  const activeId = state[TYPE].active;
  return activeId ? selectLocale(state, activeId) : undefined;
};

export const selectDraftLocales = <T extends { locales: LocalesState }>(
  state: T
): UUID[] => state[TYPE].draftLocales;
